/**
 * `ProjectDocsModel` — workflow-product document association.
 *
 * The old web port mirrored Unity's document manifest (`docs/index.json`) and
 * project-row `DocumentStoragePaths`. That association is retired. The loaded
 * `workflow.yaml` now decides the document set: `workflow.product` is
 * normalized (`normalizeProductNameKey`) before Dynamo/vector lookups, so
 * authored `' RoboDog'` matches a stored key of `robodog`. YAML is not
 * rewritten. The browser path is
 * read-only; document create/update/delete remains owned by the Define app.
 */

import { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { IProjectWorkflowModel } from "@models/workflow/IProjectWorkflowModel";
import {
  normalizeProductNameKey,
  uniqueNormalizedNameKeys,
} from "@models/data/productDocumentDto";
import type {
  IProductDocsStoreModel,
  ProductDocsQuery,
} from "./IProductDocsStoreModel";
import { PRODUCT_DOCS_STORE_UNAVAILABLE } from "./IProductDocsStoreModel";
import type {
  DocLibraryItem,
  DocLibrarySource,
  DocUploadResult,
  IProjectDocsModel,
} from "./IProjectDocsModel";

export class ProjectDocsModel implements IProjectDocsModel {
  readonly onChanged = new Emitter<ReadonlyArray<DocLibraryItem>>();
  readonly onBusyChanged = new Emitter<boolean>();
  readonly onProgressChanged = new Emitter<string>();

  private m_items: ReadonlyArray<DocLibraryItem> = [];
  private m_isBusy = false;
  private m_progress = "";
  private m_lastProductKey: string | null = null;
  private m_lastLookupSignature: string | null = null;

  constructor(
    private readonly m_project: IProjectModel,
    private readonly m_auth: IAuthModel,
    private readonly m_workflow: IProjectWorkflowModel,
    private readonly m_productDocs: IProductDocsStoreModel,
  ) {
    this.m_project.onActiveProjectChanged.on(() => {
      this.m_lastProductKey = null;
      this.m_lastLookupSignature = null;
      this.setItems([]);
    });

    this.m_workflow.onWorkflowChanged.on((event) => {
      const activeProjectId = this.m_project.activeProject?.id ?? "";
      if (event.projectId !== activeProjectId) return;
      void this.loadAsync(true);
    });
  }

  get items(): ReadonlyArray<DocLibraryItem> {
    return this.m_items;
  }

  get isBusy(): boolean {
    return this.m_isBusy;
  }

  get progress(): string {
    return this.m_progress;
  }

  get productKey(): string | null {
    return this.m_lastProductKey;
  }

  get canManageDocuments(): boolean {
    return this.m_productDocs.canWrite;
  }

  async loadAsync(
    forceReload = false,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<DocLibraryItem>> {
    const query = this.docsQuery();
    if (!query) {
      this.m_lastProductKey = null;
      this.m_lastLookupSignature = null;
      this.setItems([]);
      return this.m_items;
    }

    if (!forceReload && this.m_lastLookupSignature === query.signature) {
      return this.m_items;
    }

    this.setBusy(true);
    try {
      const [productDocs, includedDocs] = await Promise.all([
        query.productKey
          ? this.m_productDocs.getDocsForProductAsync(
            { customerId: query.customerId, productKey: query.productKey },
            signal,
          )
          : Promise.resolve([]),
        query.includeKeys.length > 0
          ? this.m_productDocs.getDocsByKeysAsync(
            { customerId: query.customerId, documentNameKeys: query.includeKeys },
            signal,
          )
          : Promise.resolve([]),
      ]);
      this.m_lastProductKey = query.productKey || null;
      this.m_lastLookupSignature = query.signature;
      this.setItems(this.sortItems(mergeDocItems([
        ...productDocs.map((item) => withSource(item, { kind: "product", productKey: query.productKey })),
        ...includedDocs.map((item) => withSource(item, {
          kind: "workflow",
          workflowId: query.workflowId,
          knowledgeKey: item.documentNameKey,
        })),
      ])));
    } catch (err) {
      console.warn("[ProjectDocsModel] product document load failed:", err);
      this.m_lastProductKey = null;
      this.m_lastLookupSignature = null;
      this.setItems([]);
    } finally {
      this.setBusy(false);
    }
    return this.m_items;
  }

  async getProjectDocsAsync(forceReload = false): Promise<ReadonlyArray<DocLibraryItem> | null> {
    return this.loadAsync(forceReload);
  }

  async uploadAsync(
    _files: ReadonlyArray<File>,
    _signal?: AbortSignal,
  ): Promise<DocUploadResult> {
    throw new Error(PRODUCT_DOCS_STORE_UNAVAILABLE);
  }

  async deleteAsync(_storagePath: string, _signal?: AbortSignal): Promise<void> {
    throw new Error(PRODUCT_DOCS_STORE_UNAVAILABLE);
  }

  async getThumbnailUrlAsync(fileName: string): Promise<string | null> {
    if (!fileName) return null;
    const query = this.docsQuery();
    if (!query) return null;
    return this.m_productDocs.getThumbnailUrlAsync(query, fileName);
  }

  async openAsync(storagePath: string): Promise<boolean> {
    if (!storagePath) return false;
    const query = this.docsQuery();
    if (!query) return false;
    return this.m_productDocs.openAsync(query, storagePath);
  }

  reset(): void {
    this.m_lastProductKey = null;
    this.m_lastLookupSignature = null;
    this.setProgress("");
    this.setBusy(false);
    this.setItems([]);
  }

  private docsQuery(): (ProductDocsQuery & {
    readonly includeKeys: ReadonlyArray<string>;
    readonly signature: string;
    readonly workflowId: string;
  }) | null {
    const workflow = this.m_workflow.loadedWorkflow;
    const productKey = normalizeProductNameKey(workflow?.product);
    const includeKeys = uniqueNormalizedNameKeys(
      workflow?.knowledgeDocs
        .filter((entry) => !entry.isInvalid && entry.doc.trim())
        .map((entry) => entry.doc) ?? [],
    );
    const project = this.m_project.activeProject;
    const customerId = project?.customerId ?? this.m_auth.currentUser?.customerId;
    if ((!productKey && includeKeys.length === 0) || !customerId || !project || !workflow) return null;
    const workflowId = workflow.uid.trim();
    return {
      customerId,
      productKey,
      includeKeys,
      workflowId,
      signature: [customerId, workflowId, productKey, ...includeKeys].join("\n"),
    };
  }

  private sortItems(items: ReadonlyArray<DocLibraryItem>): ReadonlyArray<DocLibraryItem> {
    return [...items].sort((a, b) => b.lastModifiedMs - a.lastModifiedMs);
  }

  private setItems(items: ReadonlyArray<DocLibraryItem>): void {
    this.m_items = items;
    this.onChanged.emit(items);
  }

  private setBusy(busy: boolean): void {
    if (this.m_isBusy === busy) return;
    this.m_isBusy = busy;
    this.onBusyChanged.emit(busy);
  }

  private setProgress(progress: string): void {
    if (this.m_progress === progress) return;
    this.m_progress = progress;
    this.onProgressChanged.emit(progress);
  }
}

function withSource(item: DocLibraryItem, source: DocLibrarySource): DocLibraryItem {
  if (source.kind === "product" && !source.productKey) return item;
  if (source.kind === "workflow" && !source.workflowId) return item;
  return {
    ...item,
    sources: addSource(item.sources, source),
  };
}

function mergeDocItems(items: ReadonlyArray<DocLibraryItem>): ReadonlyArray<DocLibraryItem> {
  const byStoragePath = new Map<string, DocLibraryItem>();
  for (const item of items) {
    const key = item.storagePath.trim().toLowerCase() || item.fileName.trim().toLowerCase();
    const existing = byStoragePath.get(key);
    if (!existing) {
      byStoragePath.set(key, item);
      continue;
    }
    byStoragePath.set(key, {
      ...existing,
      sources: item.sources.reduce(
        (sources, source) => addSource(sources, source),
        existing.sources,
      ),
    });
  }
  return [...byStoragePath.values()];
}

function addSource(
  sources: ReadonlyArray<DocLibrarySource>,
  source: DocLibrarySource,
): ReadonlyArray<DocLibrarySource> {
  const signature = sourceSignature(source);
  if (sources.some((candidate) => sourceSignature(candidate) === signature)) return sources;
  return [...sources, source];
}

function sourceSignature(source: DocLibrarySource): string {
  return source.kind === "product"
    ? `product:${normalizeProductNameKey(source.productKey)}`
    : `workflow:${source.workflowId.trim()}:${normalizeProductNameKey(source.knowledgeKey)}`;
}
